import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, X, Loader2, Upload } from "lucide-react";
import { API, fileToDataUrl, parseSpecs, formatApiError } from "../lib/store";

const MAX_IMAGES = { fleet: 3, projects: 4 };

const emptyForm = {
  title: "",
  description: "",
  specsText: "",
  images: [],
  newsUrl: "",
  newsSource: "",
};

export default function AdminContent({ collection, heading, useSpecs, onSessionExpired }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/content/${collection}`, { withCredentials: true });
      setItems(r.data);
    } catch (err) {
      if (err?.response?.status === 401) onSessionExpired?.();
      else setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setEditing({});
    setForm(emptyForm);
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      description: item.description || "",
      specsText: (item.specs || []).map((s) => `${s.k}: ${s.v}`).join("\n"),
      images: item.images || [],
      newsUrl: item.news_url || "",
      newsSource: item.news_source || "",
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const addFiles = async (e) => {
    const room = MAX_IMAGES[collection] - form.images.length;
    const files = [...e.target.files].slice(0, Math.max(0, room));
    if (!files.length) {
      toast.error(`Max ${MAX_IMAGES[collection]} photos per item.`);
      return;
    }
    const urls = await Promise.all(files.map((f) => fileToDataUrl(f)));
    setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
  };

  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, x) => x !== i) }));

  const save = async () => {
    if (form.title.trim().length < 2) {
      toast.error("Title is too short.");
      return;
    }
    if (collection === "projects" && form.images.length === 0) {
      toast.error("Add at least one photo.");
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      specs: useSpecs ? parseSpecs(form.specsText) : [],
      images: form.images,
      news_url: form.newsUrl.trim(),
      news_source: form.newsSource.trim(),
    };
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/content/${collection}/${editing.id}`, payload, { withCredentials: true });
        toast.success("Saved — refresh the website to see it.");
      } else {
        await axios.post(`${API}/content/${collection}`, payload, { withCredentials: true });
        toast.success("Added — refresh the website to see it.");
      }
      cancel();
      await fetchItems();
    } catch (err) {
      if (err?.response?.status === 401) onSessionExpired?.();
      else toast.error(formatApiError(err, "Could not save. Check the item and try again."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/content/${collection}/${item.id}`, { withCredentials: true });
      toast.success("Deleted.");
      if (editing?.id === item.id) cancel();
      await fetchItems();
    } catch (err) {
      if (err?.response?.status === 401) onSessionExpired?.();
      else toast.error("Could not delete.");
    }
  };

  return (
    <div data-testid={`admin-content-${collection}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-steel">
          {items.length} item{items.length === 1 ? "" : "s"} — shown on the website after you save
        </p>
        {!editing && (
          <button
            data-testid={`content-add-${collection}`}
            onClick={startNew}
            className="bg-brand px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover"
          >
            + Add New
          </button>
        )}
      </div>

      {editing && (
        <div
          data-testid={`content-form-${collection}`}
          className="mt-5 border border-line bg-surface p-6"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg uppercase tracking-tight text-slate-100">
              {editing?.id ? "Edit" : "New"} {heading} item
            </h3>
            <button
              onClick={cancel}
              aria-label="Close form"
              className="text-steel transition-colors hover:text-white"
            >
              <X size={18} />
            </button>
          </div>

          <label className="mt-5 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
            Title *
          </label>
          <input
            data-testid={`content-title-input-${collection}`}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Marine Response Vessel"
            className="mt-2 w-full border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
          />

          <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
            Description
          </label>
          <textarea
            data-testid={`content-desc-input-${collection}`}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            placeholder="Short description..."
            className="mt-2 w-full resize-y border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
          />

          {useSpecs && (
            <>
              <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
                Specs (one per line — Label: Value)
              </label>
              <textarea
                data-testid={`content-specs-input-${collection}`}
                value={form.specsText}
                onChange={(e) => setForm({ ...form, specsText: e.target.value })}
                rows={3}
                placeholder={"Type: Launch / Pilot Boat\nStatus: Active"}
                className="mt-2 w-full resize-y border border-line bg-transparent px-4 py-3 font-mono text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
              />
            </>
          )}

          <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
            Photos (max {MAX_IMAGES[collection]})
          </label>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative">
                <img
                  src={img}
                  alt={`upload ${i + 1}`}
                  className="h-16 w-24 border border-line object-cover"
                />
                <button
                  data-testid={`content-image-remove-${collection}-${i}`}
                  onClick={() => removeImage(i)}
                  className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center bg-brand text-white"
                  aria-label="Remove photo"
                >
                  <X size={11} />
                </button>
              </div>
            ))}
            {form.images.length < MAX_IMAGES[collection] && (
              <label className="flex h-16 w-24 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-line text-steel transition-colors hover:border-brand hover:text-brand">
                <Upload size={16} />
                <span className="font-mono text-[8px] uppercase tracking-[0.14em]">
                  Upload
                </span>
                <input
                  data-testid={`content-images-input-${collection}`}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={addFiles}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {collection === "projects" && (
            <>
              <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
                News story link (optional)
              </label>
              <input
                data-testid={`content-newsurl-input-${collection}`}
                value={form.newsUrl}
                onChange={(e) => setForm({ ...form, newsUrl: e.target.value })}
                placeholder="https://news-site.com/story..."
                className="mt-2 w-full border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
              />
              <label className="mt-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel">
                News source name
              </label>
              <input
                data-testid={`content-newssource-input-${collection}`}
                value={form.newsSource}
                onChange={(e) => setForm({ ...form, newsSource: e.target.value })}
                placeholder="e.g. Astro Awani"
                className="mt-2 w-full border border-line bg-transparent px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-brand"
              />
            </>
          )}

          <div className="mt-6 flex gap-3">
            <button
              data-testid={`content-save-${collection}`}
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-brand px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {editing?.id ? "Save Changes" : "Add Item"}
            </button>
            <button
              onClick={cancel}
              className="border border-line px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-steel transition-colors hover:border-slate-100 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="mt-8 flex items-center gap-2 text-sm text-steel">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : (
        <div className="mt-6 space-y-3 pb-16">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`content-item-${collection}`}
              className="flex items-start justify-between gap-4 border border-line bg-surface p-5"
            >
              <div className="flex gap-4">
                {item.images?.[0] && (
                  <img
                    src={item.images[0]}
                    alt=""
                    className="h-16 w-24 shrink-0 border border-line object-cover"
                  />
                )}
                <div>
                  <div className="font-display text-lg uppercase tracking-tight text-slate-100">
                    {item.title}
                  </div>
                  {item.description && (
                    <p className="mt-1 max-w-xl text-xs font-light leading-relaxed text-steel">
                      {item.description}
                    </p>
                  )}
                  {item.news_url && (
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.14em] text-brand">
                      Press: {item.news_source || "news"} →
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`content-edit-${collection}`}
                  onClick={() => startEdit(item)}
                  aria-label={`Edit ${item.title}`}
                  className="border border-line p-2.5 text-steel transition-colors hover:border-brand hover:text-brand"
                >
                  <Pencil size={13} />
                </button>
                <button
                  data-testid={`content-delete-${collection}`}
                  onClick={() => remove(item)}
                  aria-label={`Delete ${item.title}`}
                  className="border border-line p-2.5 text-steel transition-colors hover:border-brand hover:text-brand"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
          {!items.length && (
            <p className="text-sm text-steel">
              Nothing here yet — the website is showing its built-in content. Add
              an item and it will take over.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
